import type { ReactNode } from 'react'
import { Form, Input, InputNumber, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { AgentSelect } from '@/features/agent/AgentSelect'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { DELIVERY_METHODS, PHONE_E164, normalizePhone, type DeliveryMethod } from './subscriberApi'

/**
 * Yaratish va tahrirlash formalari UMUMIY qismi.
 *
 * NEGA AJRATILGAN: ikkala forma ham bir xil `SubscriberDraft` ni to'playdi
 * va bir xil tekshiruvlarni qo'llaydi. Ular nusxa bo'lganda naqsh va
 * chegaralar ALBATTA ajrashadi — masalan yaratishdagi statik IP naqshi
 * tuzatilib, tahrirlashdagisi eski qolardi, va operator xatoni faqat
 * serverdan (tanasiz 400 bilan) ko'rardi.
 *
 * ⚠️ Bu komponent Form.Item'larni chizadi, `Form` ni EMAS — chaqiruvchi
 * uni o'z `Form` i ichiga qo'yadi va yuborishni o'zi boshqaradi.
 */
export interface SubscriberFieldValues {
  login: string
  /** Faqat `create` rejimida so'raladi */
  password?: string
  displayName?: string
  phone?: string
  kind: DeliveryMethod
  /**
   * Abonent qaysi agent/resellerga biriktiriladi (`platform.org_node.id`).
   *
   * ⚠️ `null` — «biriktirilmagan» va bu XATO EMAS. Batafsil
   * {@link AgentSelect} javadoc'ida.
   *
   * ⚠️ Bu maydonni forma to'plagani BILAN chaqiruvchi uni YUBORISHI ham
   * kerak. Uni to'plab, so'rovga qo'shmaslik eng yomon variant bo'lardi:
   * operator agentni tanlaydi, panel «yaratildi» deydi, `agent_id` esa
   * bo'sh qoladi — ya'ni tuzatilayotgan nuqson aynan o'z shaklida
   * qaytadi, faqat endi ekranda «tanladim-ku» degan dalil bilan.
   */
  agentId?: string | null
  simultaneousUse?: number | null
  staticIp?: string
}

interface SubscriberFormFieldsProps {
  /**
   * `create` — login tahrirlanadi, parol so'raladi.
   *
   * `edit` — login FAQAT KO'RSATILADI va parol maydoni YO'Q:
   *   · parol alohida amalda (`changePassword`) — u audit jurnaliga
   *     boshqacha yoziladi va boshqa huquq talab qiladi
   *     (`subscriber.password.set`)
   *   · login RADIUS proyeksiyasining kaliti. Uni o'zgartirish
   *     `radcheck` da ESKI qatorni qoldirib ketadi (proyeksiya hodisasi
   *     faqat YANGI loginni olib keladi), ya'ni abonent eski login bilan
   *     ham ulanaverardi. Backend bunga ruxsat beradi, panel esa ataylab
   *     bermaydi — tuzatib bo'lmaydigan holatga olib kelmasin.
   */
  mode: 'create' | 'edit'
  /**
   * ⛔ Telefon NIQOBLANGAN va hali ochilmagan — maydon TAHRIRLANMAYDI.
   *
   * Sabab `subscriberApi.MaskedPhoneError` da: `PUT` to'liq
   * almashtirish va `phone = :phone` `COALESCE` SIZ yoziladi, ya'ni
   * niqob bilan saqlash raqamni JIMGINA yo'q qilardi. Maydonni ochiq
   * qoldirib «yubormaymiz» deb kelishish yetarli emas — operator
   * niqobning ustiga yozib, o'zi bilmagan holda raqamni buzardi.
   */
  phoneLocked?: boolean
  /** Ism-familiya kiritilgan bo'lsa nom ulardan olinadi — alohida maydon kerak emas */
  showDisplayName?: boolean
  /** Turdan KEYIN, chegaralardan OLDIN chiziladigan qo'shimcha maydonlar */
  children?: ReactNode
  /** Kvotali VISP: login va parol o'rniga hovuzdagi hisob tanlanadi */
  accountPicker?: ReactNode
}

export function SubscriberFormFields({
  mode,
  phoneLocked = false,
  showDisplayName = true,
  children,
  accountPicker,
}: SubscriberFormFieldsProps) {
  const { t } = useTranslation()
  const isCreate = mode === 'create'

  return (
    <>
      {isCreate && accountPicker ? (
        <Form.Item
          name="login"
          label={t('subscriber.pool.account')}
          extra={t('subscriber.pool.accountHint')}
          rules={[{ required: true, message: t('subscriber.pool.accountRequired') }]}
        >
          {accountPicker}
        </Form.Item>
      ) : (
        <Form.Item
          name="login"
          label={t('operation.fieldUsername')}
          /*
            ⚠️ BU IZOH «Batafsil» OSTIGA QO'YILMAGAN — ATAYLAB (XSB-004).

            Chegara brauzerda O'LCHANDI: «Batafsil» tugmasi bitta qator
            egallaydi, ya'ni uch qatorlik matnda «2 qator + tugma» oddiy uch
            qatordan BALANDROQ chiqadi. Foyda faqat ~200 belgidan boshlanadi,
            bu matn esa undan qisqa (uz 152, en 195, ru 149). Formadagi
            yagona istisno — statik IP izohi (221 belgi), pastga qarang.
          */
          extra={isCreate ? undefined : t('subscriber.edit.loginLocked')}
          rules={
            isCreate
              ? [
                  { required: true, message: t('operation.validation.usernameRequired') },
                  {
                    // Backenddagi `SubscriberDraft` naqshining AYNAN o'zi. Uni bu
                    // yerda takrorlash ataylab: server rad javobi tanasiz 401 bo'lib
                    // kelardi (`client.ts` izohi), ya'ni operator sababini
                    // ko'rmasdi. Bu tekshiruv esa xatoni maydonning yonida aytadi.
                    pattern: /^[A-Za-z0-9._@-]{1,64}$/,
                    message: t('operation.validation.usernameFormat'),
                  },
                ]
              : []
          }
        >
          {/* ⚠️ `disabled`, olib tashlangan EMAS: qiymat forma do'konida qoladi
              va `PUT` ga o'zgarishsiz qaytadi. Maydonni butunlay olib tashlash
              `login` ni bo'sh yuborardi va backend uni 400 bilan rad etardi. */}
          <Input autoFocus={isCreate} autoComplete="off" disabled={!isCreate} />
        </Form.Item>
      )}

      {isCreate && !accountPicker ? (
        <Form.Item
          name="password"
          label={t('operation.fieldPassword')}
          /*
            ⚠️ IZOH OLIB TASHLANDI va bu MA'LUMOT YO'QOTISH EMAS (XSB-004).

            Bu yerda `actions.password.currentNotShown` turardi: «Joriy parol
            hech qachon ko'rsatilmaydi — server uni qaytarmaydi ham».
            YARATISH formasida «joriy parol» degan narsaning O'ZI YO'Q —
            hisob hali mavjud emas. Ya'ni izoh bo'sh joy egallashdan tashqari
            operatorni chalg'itardi ham: u "qayerdadir joriy parol bor" degan
            taassurot berardi.

            Matn o'chirilmadi — u AYNI shu gap to'g'ri bo'lgan joyda qoladi:
            abonent kartochkasidagi parol bo'limi (`SubscriberDetailPage`).
          */
          rules={[
            { required: true, message: t('operation.validation.passwordRequired') },
            { min: 4, max: 128, message: t('operation.validation.passwordLength') },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      ) : null}

      {showDisplayName ? (
        <Form.Item name="displayName" label={t('subscribers.columns.displayName')}>
          <Input autoFocus={!isCreate} autoComplete="off" />
        </Form.Item>
      ) : null}

      {/*
        Telefon — MAJBURIY EMAS va bu ataylab.

        Bazadagi mavjud abonentlarda raqam yo'q. Maydonni majburiy
        qilsak, operator faqat nomni to'g'rilamoqchi bo'lganda ham
        raqam so'ralardi — va u ko'pincha ma'lum emas, ya'ni yozuvni
        umuman saqlab bo'lmasdi.

        ⚠️ Lekin bo'shliq ko'rinmay qolmaydi: ro'yxatda «Telefoni
        yo'q» filtri bor va operator ularni to'ldirib chiqa oladi.
      */}
      <Form.Item
        name="phone"
        label={t('subscriber.phone')}
        extra={t('subscriber.phoneHint')}
        rules={[
          {
            /*
              ⚠️ Oddiy `pattern` YETMAYDI va sabab amaliy: operator
              raqamni odatda `+998 90 123 45 67` ko'rinishida yozadi.
              Backend uni tozalab qabul qiladi (`Phones.normalize`),
              panel esa qat'iy naqsh bilan RAD ETARDI — ya'ni panel
              serverdan qattiqroq bo'lib qolardi va operator to'g'ri
              raqamni kirita olmasdi.

              Shuning uchun bu yerda ham AVVAL tozalanadi, keyin
              tekshiriladi — backend bilan bir xil tartibda.

              ⚠️ Bo'sh qiymat O'TADI: maydon ixtiyoriy va raqamni
              tozalash — haqiqiy amal, xato emas.
            */
            validator: (_, value: string | undefined) => {
              const clean = normalizePhone(value)
              if (clean === null || PHONE_E164.test(clean)) return Promise.resolve()
              return Promise.reject(new Error(t('subscriber.phoneFormat')))
            },
          },
        ]}
      >
        <Input autoComplete="off" inputMode="tel" disabled={phoneLocked} />
      </Form.Item>

      <Form.Item
        name="kind"
        label={t('subscribers.columns.kind')}
        rules={[{ required: true }]}
        extra={t('operation.kindHint')}
      >
        <Select
          options={DELIVERY_METHODS.map((k) => ({ value: k, label: t(`deliveryMethod.${k}`) }))}
        />
      </Form.Item>

      {/*
        AGENT — filial daromadi zanjirining birinchi bo'g'ini.

        ⚠️ MAJBURIY EMAS va bu ataylab: «operator to'g'ridan-to'g'ri
        sotdi» haqiqiy holat. Yulduzcha qo'yish esa operatorni tasodifiy
        agent tanlashga majburlardi — komissiya va filial daromadi
        noto'g'ri tugunga yozilardi, ya'ni bo'sh maydondan YOMONROQ.

        ⚠️ Tozalash tugmasi faqat YARATISHDA. Tahrirlashda backend
        `COALESCE` ishlatadi, ya'ni bo'sh yuborilgan qiymat egani
        o'chirmaydi — tugma qoldirilsa panel bajarilmagan amalni
        bajarildi deb ko'rsatardi (`AgentSelect.clearable` izohi).
      */}
      <Form.Item
        name="agentId"
        label={t('subscriber.agent.label')}
        extra={isCreate ? t('subscriber.agent.createHint') : t('subscriber.agent.editHint')}
      >
        <AgentSelect clearable={isCreate} />
      </Form.Item>

      {children}

      <Form.Item
        name="simultaneousUse"
        label={t('terms.simultaneousUseLabel')}
        /*
          ⚠️ ILGARI BU YERDA `actions.simultaneousUse.fromTariff` TURARDI va
          ekranda XOM NAQSH ko'rinardi: «Tarifda: {{value}}».

          Sabab: o'sha kalit qiymat MA'LUM bo'lgan joy uchun yozilgan
          (abonent kartochkasi), bu yerda esa tarif hali tanlanmagan ham
          bo'lishi mumkin — argument berilmasa i18next naqshni O'Z HOLICHA
          qoldiradi. Xato jimgina edi: kompilyator ham, sinovlar ham hech
          nima demasdi, faqat operator formada `{{value}}` ni ko'rardi.
        */
        extra={t('actions.simultaneousUse.createHint')}
      >
        <InputNumber min={1} style={{ width: '100%' }} placeholder={t('deviceLimit.fromTariff')} />
      </Form.Item>

      <Form.Item
        name="staticIp"
        /*
          ⚠️ `terms.staticIp` — YORLIQ EMAS, TAVSIF: "Abonentga har
          ulanishda pool o'rniga beriladigan doimiy IP…". U yorliq
          o'rniga qo'yilgani uchun formada butun gap yorliq bo'lib
          chizilardi. Yorliq `terms.staticIpLabel`, tavsif esa pastda
          `extra` da turadi.
        */
        label={t('terms.staticIpLabel')}
        extra={<ExpandableHint text={t('actions.staticIp.hint')} fontSize={14} />}
        rules={[
          {
            pattern: /^$|^(\d{1,3}\.){3}\d{1,3}$/,
            message: t('actions.staticIp.validationFormat'),
          },
        ]}
      >
        <Input placeholder={t('actions.staticIp.remove')} />
      </Form.Item>
    </>
  )
}
